import React, { useState, useEffect } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import { Checkbox } from "./checkbox";
import { Button } from "./button";
import dayjs from "dayjs";

/**
 * 通用樹狀選擇器 (V6)
 * ✅ 每層節點有自己的 valueKey
 * ✅ 回傳結構為 { build: [...], room: [...], device: [...] }
 * ✅ 支援 defaultSelected
 * ✅ showCheckbox 可個別控制
 * ✅ showCount 支援顯示節點右側數量 badge（節點資料需有 count 欄位）
 */
let demoData = [
    {
        "id": "build_1",
        "valueKey": "build",
        "showCheckbox": false,
        "label": "第一教學館",
        "value": 1,
        "children": [
            {
                "id": "floor_1",
                "valueKey": "floor",
                "showCheckbox": false,
                "label": "2 樓",
                "value": 2,
                "children": [
                    {
                        "id": "room_1",
                        "valueKey": "room",
                        "showCheckbox": true,
                        "label": "研究小間 201",
                        "value": 201,
                        "count": 3,
                        "children": [
                            {
                                "id": "device_1",
                                "valueKey": "device",
                                "showCheckbox": true,
                                "label": "門禁卡機",
                                "value": 1,
                                "count": 1
                            },
                            {
                                "id": "device_2",
                                "valueKey": "device",
                                "showCheckbox": true,
                                "label": "攝影機",
                                "value": 2,
                                "count": 2
                            }
                        ]
                    },
                    {
                        "id": "room_2",
                        "valueKey": "room",
                        "showCheckbox": true,
                        "label": "研究小間 202",
                        "value": 202,
                        "count": 0,
                        "children": []
                    }
                ]
            }
        ]
    }
]

export const BaseTreeSelector = ({
    title = "分類篩選",
    data = demoData,
    defaultSelected = {}, // e.g. { build:[1], room:[1], device:[1,2] }
    onChange = () => { },
    name,
    propsShowMode = ['checkbox'],
    showNodeDetails = false, // 是否顯示節點的額外資訊（description、startDate、endDate）
    showNodeDateRange = false, // 是否顯示節點的日期範圍（startDate、endDate）
    onNodeClick = null, // 節點標籤點擊事件，傳入則啟用點擊模式
    selectionMode = 'multi', // 'single' 或 'multi' - 控制單選或多選
    onSelectionChange = null, // 單選模式專用：選擇變更時的回調
    showCount = false, // ✅ 新增：是否顯示節點右側的數量 badge（節點資料需有 count 欄位）
}) => {
    const [expanded, setExpanded] = useState(new Set());
    const [selectedMap, setSelectedMap] = useState(defaultSelected);

    // 預設展開所有有 children 的節點
    useEffect(() => {
        const ids = [];
        const collectExpanded = (nodes) => {
            nodes.forEach((n) => {
                if (n.children?.length) {
                    ids.push(n.id);
                    collectExpanded(n.children);
                }
            });
        };
        collectExpanded(data);
        setExpanded(new Set(ids));
    }, []);

    const toggleExpand = (id) => {
        const next = new Set(expanded);
        next.has(id) ? next.delete(id) : next.add(id);
        setExpanded(next);
    };

    const handleCheck = (node, checked) => {
        const { valueKey, value, label } = node;

        // 單選模式：只保留一個選擇
        if (selectionMode === 'single') {
            const nextMap = {};
            if (checked) {
                nextMap[valueKey] = [value];
                setSelectedMap(nextMap);
                onSelectionChange?.({
                    valueKey,
                    value,
                    label,
                    node
                });
            }
            return;
        }

        // 多選模式
        const nextMap = structuredClone(propsShowMode?.includes('checkbox') ? selectedMap : {});

        if (!nextMap[valueKey]) nextMap[valueKey] = [];

        if (checked) {
            if (!nextMap[valueKey].includes(value)) {
                nextMap[valueKey].push(value);
            }
            // 勾選時，連同所有子節點
            if (node.children?.length) {
                collectValues(node.children).forEach(({ valueKey, value }) => {
                    if (!nextMap[valueKey]) nextMap[valueKey] = [];
                    if (!nextMap[valueKey].includes(value)) nextMap[valueKey].push(value);
                });
            }
        } else {
            nextMap[valueKey] = nextMap[valueKey].filter((v) => v !== value);
            // 取消時，也移除所有子節點
            if (node.children?.length) {
                collectValues(node.children).forEach(({ valueKey, value }) => {
                    nextMap[valueKey] = (nextMap[valueKey] || []).filter(
                        (v) => v !== value
                    );
                });
            }
        }

        setSelectedMap(nextMap);
        onChange?.({
            ...nextMap,
            target: {
                name,
                value: value
            }
        }, label);
    };

    const collectValues = (nodes) => {
        let list = [];
        nodes.forEach((n) => {
            list.push({ valueKey: n.valueKey, value: n.value });
            if (n.children?.length) {
                list = list.concat(collectValues(n.children));
            }
        });
        return list;
    };

    const isChecked = (node) => {
        const arr = selectedMap[node.valueKey] || [];
        return arr.includes(node.value);
    };

    const renderNodes = (nodes, level = 0) =>
        nodes.map((node) => {
            const hasChildren = node.children?.length > 0;
            const isExpanded = expanded.has(node.id);
            const checked = isChecked(node);
            const showCheckbox = propsShowMode?.includes('checkbox') ?? node.showCheckbox ?? true;

            return (
                <div key={node.id} className="ml-1 sm:ml-2"
                    {
                    ...(!propsShowMode?.includes('checkbox') ?
                        {
                            onClick: () => handleCheck(node, !checked),
                        } : {}
                    )
                    }
                >
                    <div className={`flex items-start sm:items-center gap-1 py-2 px-2 hover:bg-accent/30 rounded transition-colors ${checked ? 'bg-accent/40' : ''}`}>
                        {hasChildren ? (
                            <div
                                onClick={(e) => {
                                    e.stopPropagation();
                                    toggleExpand(node.id);
                                }}
                                className="cursor-pointer flex-shrink-0 mt-0.5 sm:mt-0"
                            >
                                {isExpanded ? (
                                    <ChevronDown className="w-4 h-4 text-gray-500" />
                                ) : (
                                    <ChevronRight className="w-4 h-4 text-gray-500" />
                                )}
                            </div>
                        ) : (
                            <div className="w-4 flex-shrink-0" />
                        )}

                        {showCheckbox && (
                            <Checkbox
                                checked={checked}
                                onCheckedChange={(val) => handleCheck(node, !!val)}
                                className="flex-shrink-0 mt-0.5 sm:mt-0"
                            />
                        )}

                        {/* ✅ label 區塊：加上 justify-between 以讓 count badge 靠右 */}
                        <div
                            className={`flex-1 min-w-0 flex items-center justify-between gap-2 ${onNodeClick ? 'cursor-pointer hover:text-primary transition-colors' : ''}`}
                            onClick={(e) => {
                                if (onNodeClick) {
                                    e.stopPropagation();
                                    onNodeClick(node);
                                }
                            }}
                        >
                            {/* 左側：label + 詳細資訊 */}
                            <div className="min-w-0">
                                <span className="text-sm font-medium break-words">{node.label}</span>
                                {showNodeDetails && node.description && (
                                    <div className="text-xs text-gray-500 mt-1 break-words line-clamp-2">{node.description}</div>
                                )}
                                {showNodeDateRange && (node.startDate || node.endDate) && (
                                    <div className="text-xs text-indigo-600 font-semibold mt-1.5 space-y-0.5">
                                        {node.startDate && (
                                            <div>開始時間： {dayjs(node.startDate).format('YYYY-MM-DD')}</div>
                                        )}
                                        {node.endDate && (
                                            <div>結束時間： {dayjs(node.endDate).format('YYYY-MM-DD')}</div>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* ✅ 右側：count badge */}
                            {showCount && node.count !== undefined && (
                                <span className="flex-shrink-0 inline-flex items-center justify-center min-w-[1.5rem] h-6 px-1.5 rounded bg-gray-100 text-xs font-semibold text-gray-600">
                                    {node.count}
                                </span>
                            )}
                        </div>
                    </div>

                    {
                        hasChildren && isExpanded && (
                            <div className="ml-2 sm:ml-4 border-l border-gray-200 pl-1 sm:pl-2">
                                {renderNodes(node.children, level + 1)}
                            </div>
                        )
                    }
                </div>
            );
        });

    return (
        <div className="bg-white border rounded-lg p-2 sm:p-3 overflow-hidden">
            {title && <h3 className="text-sm sm:text-base font-semibold mb-2 px-1">{title}</h3>}
            <div className="overflow-x-auto">
                {renderNodes(data)}
            </div>
        </div>
    );
};
